import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles } from "lucide-react";
import toast from "react-hot-toast";
import './EquipmentsManager.css';
import { CATEGORIES, SUGGESTED_EQUIPMENTS, detectCategory } from "../../constants/equipments"; // 🆕 source unique, ajuste le chemin

// ─── Normalisation legacy strings → {name, category} ─────────────────────────
function normalizeEquipments(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map(item =>
    typeof item === "string"
      ? { name: item, category: detectCategory(item) }
      : item
  );
}
// 🆕 CATEGORIES, SUGGESTED_EQUIPMENTS, CATEGORY_KEYWORDS et detectCategory
// ne sont plus déclarés en local ici — ils viennent du fichier partagé ci-dessus

// ─── Composant ────────────────────────────────────────────────────────────────
export default function EquipmentsManager({ data, hotelId }) {
  const [equipments, setEquipments]         = useState([]);
  const [inputValue, setInputValue]         = useState("");
  const [selectedCategory, setSelectedCategory] = useState("auto");
  const [suggestion, setSuggestion]         = useState(null);
  const [showCatDropdown, setShowCatDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (data?.equipments) setEquipments(normalizeEquipments(data.equipments));
  }, [data]);

  useEffect(() => {
    const handle = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowCatDropdown(false);
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const saveToBackend = async (updated) => {
    await fetch(`https://api.komorotel.com/api/hotels/${hotelId}`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ equipments: updated }),
    });
  };

  const resolveCategory = (name) =>
    selectedCategory === "auto" ? detectCategory(name) : selectedCategory;

  const addItems = async (names, catOverride = null) => {
    const newItems = names
      .map(n => n.trim()).filter(n => n !== "")
      .filter(n => !equipments.some(e => e.name.toLowerCase() === n.toLowerCase()))
      .map(n => ({ name: n, category: catOverride ?? resolveCategory(n) }));

    if (newItems.length === 0) { toast.error("Équipement(s) déjà présent(s)"); return; }

    const updated = [...equipments, ...newItems];
    setEquipments(updated);
    setInputValue("");
    setSuggestion(null);
    setSelectedCategory("auto");
    await saveToBackend(updated);
    toast.success("Équipement(s) ajouté(s) ✨");
  };

  const handleAdd = () => { if (inputValue.trim()) addItems(inputValue.split(",")); };

  const handleDelete = async (name) => {
    const updated = equipments.filter(e => e.name !== name);
    setEquipments(updated);
    await saveToBackend(updated);
    toast.success("Équipement supprimé");
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (value.includes(",")) { addItems(value.split(",")); return; }
    setInputValue(value);
    if (!value.trim()) { setSuggestion(null); return; }
    const match = SUGGESTED_EQUIPMENTS.find(
      s => s.name.toLowerCase().startsWith(value.toLowerCase()) &&
           !equipments.some(eq => eq.name === s.name)
    );
    setSuggestion(match || null);
  };

  const grouped = CATEGORIES.reduce((acc, cat) => {
    const items = equipments.filter(e => e.category === cat.id);
    if (items.length > 0) acc.push({ ...cat, items });
    return acc;
  }, []);

  const activeCatId = selectedCategory === "auto"
    ? (inputValue.trim() ? detectCategory(inputValue) : null)
    : selectedCategory;
  const activeCat = CATEGORIES.find(c => c.id === activeCatId);

  return (
    <div className="modern-card">
      <div className="section-title">Équipements</div>

      <div className="eq-groups">
        <AnimatePresence>
          {grouped.map(cat => {
            const { Icon } = cat;
            return (
              <motion.div
                key={cat.id}
                className="eq-group"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
              >
                <div className="eq-group-label">
                  <span className="eq-group-icon-wrap">
                    <Icon size={13} strokeWidth={2.2} />
                  </span>
                  {cat.label}
                </div>
                <div className="equipments-container">
                  <AnimatePresence>
                    {cat.items.map(item => (
                      <motion.div
                        key={item.name}
                        layout
                        initial={{ opacity: 0, scale: 0.85 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.85 }}
                        className="equipment-tag"
                      >
                        {item.name}
                        <button onClick={() => handleDelete(item.name)} aria-label="Supprimer">✕</button>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>

        {equipments.length === 0 && (
          <p className="eq-empty">Aucun équipement ajouté pour l'instant.</p>
        )}
      </div>

      <div className="eq-add-bar">
        <div className="eq-cat-picker" ref={dropdownRef}>
          <button
            className="eq-cat-btn"
            onClick={() => setShowCatDropdown(v => !v)}
            type="button"
            title="Choisir une catégorie"
          >
            <span className="eq-cat-btn-icon">
              {activeCat
                ? <activeCat.Icon size={13} strokeWidth={2.2} />
                : <Sparkles size={13} strokeWidth={2.2} />
              }
            </span>
            <span className="eq-cat-btn-label">
              {selectedCategory === "auto"
                ? (activeCat ? `Auto · ${activeCat.label}` : "Auto-détect")
                : activeCat?.label}
            </span>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M2 3.5L5 6.5L8 3.5" stroke="#2F855A" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>

          <AnimatePresence>
            {showCatDropdown && (
              <motion.div
                className="eq-cat-dropdown"
                initial={{ opacity: 0, y: -6, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -6, scale: 0.98 }}
                transition={{ duration: 0.15 }}
              >
                <div
                  className={`eq-cat-option ${selectedCategory === "auto" ? "active" : ""}`}
                  onClick={() => { setSelectedCategory("auto"); setShowCatDropdown(false); }}
                >
                  <span className="eq-cat-option-icon">
                    <Sparkles size={14} strokeWidth={2} />
                  </span>
                  Auto-détect
                </div>
                <div className="eq-cat-divider" />
                {CATEGORIES.map(cat => {
                  const { Icon } = cat;
                  return (
                    <div
                      key={cat.id}
                      className={`eq-cat-option ${selectedCategory === cat.id ? "active" : ""}`}
                      onClick={() => { setSelectedCategory(cat.id); setShowCatDropdown(false); }}
                    >
                      <span className="eq-cat-option-icon">
                        <Icon size={14} strokeWidth={2} />
                      </span>
                      {cat.label}
                    </div>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="equipment-input-wrapper">
          <input
            type="text"
            placeholder="Ajouter un équipement... (virgule pour plusieurs)"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            className="equipment-input"
          />
          {suggestion && (
            <motion.div
              className="inline-suggestion"
              onClick={() => addItems([suggestion.name], suggestion.category)}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
            >
              + {suggestion.name}
            </motion.div>
          )}
          <button className="add-btn" onClick={handleAdd} aria-label="Ajouter">+</button>
        </div>
      </div>
    </div>
  );
}